// pages/AuditLogPage.js
// Visível apenas para super_admin — exibe o log de auditoria LGPD

import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const ACOES_LABEL = {
  visualizou:           { label: 'Visualizou',           cor: '#6B7280' },
  criou:                { label: 'Criou',                 cor: '#2563EB' },
  editou:               { label: 'Editou',                cor: '#D97706' },
  assinou:              { label: 'Assinou',               cor: '#059669' },
  exportou:             { label: 'Exportou',              cor: '#7C3AED' },
  revogou_tcle:         { label: 'Revogou TCLE',          cor: '#DC2626' },
  solicitacao_titular:  { label: 'Solicitação Titular',   cor: '#0891B2' },
  login:                { label: 'Login',                  cor: '#16A34A' },
  logout:               { label: 'Logout',                 cor: '#9CA3AF' },
  erro_acesso:          { label: 'Erro de Acesso',         cor: '#EF4444' },
};

const POR_PAGINA = 50;

export default function AuditLogPage() {
  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [pagina, setPagina]       = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]           = useState(null);

  // Filtros
  const [filtroAcao, setFiltroAcao]       = useState('');
  const [filtroTabela, setFiltroTabela]   = useState('');
  const [filtroEmail, setFiltroEmail]     = useState('');
  const [filtroInicio, setFiltroInicio]   = useState('');
  const [filtroFim, setFiltroFim]         = useState('');

  // Log expandido
  const [expandido, setExpandido] = useState(null);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      let query = supabase
        .from('audit_log')
        .select('*', { count: 'exact' })
        .order('criado_em', { ascending: false })
        .range(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA - 1);

      if (filtroAcao)   query = query.eq('acao', filtroAcao);
      if (filtroTabela) query = query.ilike('tabela', `%${filtroTabela}%`);
      if (filtroEmail)  query = query.ilike('usuario_email', `%${filtroEmail}%`);
      if (filtroInicio) query = query.gte('criado_em', filtroInicio);
      if (filtroFim)    query = query.lte('criado_em', filtroFim + 'T23:59:59');

      const { data, count, error } = await query;
      if (error) throw error;

      setLogs(data || []);
      setTotal(count || 0);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtroAcao, filtroTabela, filtroEmail, filtroInicio, filtroFim]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const limparFiltros = () => {
    setFiltroAcao('');
    setFiltroTabela('');
    setFiltroEmail('');
    setFiltroInicio('');
    setFiltroFim('');
    setPagina(0);
  };

  const totalPaginas = Math.ceil(total / POR_PAGINA);

  return (
    <div style={s.page}>
      {/* Cabeçalho */}
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Log de Auditoria</h1>
          <p style={s.subtitulo}>
            Rastreabilidade de acessos e operações — LGPD Art. 46º
          </p>
        </div>
        <div style={s.badge}>
          <span style={s.badgeDot} />
          {total.toLocaleString('pt-BR')} registros
        </div>
      </div>

      {/* Filtros */}
      <div style={s.filtros}>
        <select
          style={s.select}
          value={filtroAcao}
          onChange={e => { setFiltroAcao(e.target.value); setPagina(0); }}
        >
          <option value="">Todas as ações</option>
          {Object.entries(ACOES_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <input
          style={s.input}
          placeholder="Tabela..."
          value={filtroTabela}
          onChange={e => { setFiltroTabela(e.target.value); setPagina(0); }}
        />

        <input
          style={s.input}
          placeholder="E-mail do usuário..."
          value={filtroEmail}
          onChange={e => { setFiltroEmail(e.target.value); setPagina(0); }}
        />

        <input
          style={s.input}
          type="date"
          value={filtroInicio}
          onChange={e => { setFiltroInicio(e.target.value); setPagina(0); }}
        />

        <input
          style={s.input}
          type="date"
          value={filtroFim}
          onChange={e => { setFiltroFim(e.target.value); setPagina(0); }}
        />

        {(filtroAcao || filtroTabela || filtroEmail || filtroInicio || filtroFim) && (
          <button style={s.btnLimpar} onClick={limparFiltros}>
            ✕ Limpar
          </button>
        )}
      </div>

      {/* Erro */}
      {erro && (
        <div style={s.erroBox}>
          <strong>Erro ao carregar logs:</strong> {erro}
        </div>
      )}

      {/* Tabela */}
      <div style={s.tabelaWrap}>
        <table style={s.tabela}>
          <thead>
            <tr>
              {['Data/Hora', 'Ação', 'Usuário', 'Role', 'Tabela', 'Registro', ''].map(col => (
                <th key={col} style={s.th}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {carregando ? (
              <tr>
                <td colSpan={7} style={s.tdCentro}>Carregando...</td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} style={s.tdCentro}>Nenhum registro encontrado.</td>
              </tr>
            ) : (
              logs.map(log => {
                const acaoInfo = ACOES_LABEL[log.acao] || { label: log.acao, cor: '#6B7280' };
                const isExpandido = expandido === log.id;

                return (
                  <React.Fragment key={log.id}>
                    <tr style={s.tr}>
                      <td style={s.td}>
                        <span style={s.data}>
                          {new Date(log.criado_em).toLocaleDateString('pt-BR')}
                        </span>
                        <br />
                        <span style={s.hora}>
                          {new Date(log.criado_em).toLocaleTimeString('pt-BR')}
                        </span>
                      </td>

                      <td style={s.td}>
                        <span style={{ ...s.pill, backgroundColor: acaoInfo.cor + '18', color: acaoInfo.cor, border: `1px solid ${acaoInfo.cor}40` }}>
                          {acaoInfo.label}
                        </span>
                      </td>

                      <td style={s.td}>
                        <span style={s.email}>{log.usuario_email || '—'}</span>
                      </td>

                      <td style={s.td}>
                        <span style={s.role}>{log.usuario_role || '—'}</span>
                      </td>

                      <td style={s.td}>
                        <span style={s.mono}>{log.tabela || '—'}</span>
                      </td>

                      <td style={s.td}>
                        <span style={s.mono}>
                          {log.registro_id ? log.registro_id.slice(0, 8) + '…' : '—'}
                        </span>
                      </td>

                      <td style={s.td}>
                        {log.detalhes && (
                          <button
                            style={s.btnDetalhe}
                            onClick={() => setExpandido(isExpandido ? null : log.id)}
                          >
                            {isExpandido ? '▲' : '▼'}
                          </button>
                        )}
                      </td>
                    </tr>

                    {isExpandido && log.detalhes && (
                      <tr>
                        <td colSpan={7} style={s.tdDetalhe}>
                          <pre style={s.pre}>
                            {JSON.stringify(log.detalhes, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div style={s.paginacao}>
          <button
            style={s.btnPag}
            disabled={pagina === 0}
            onClick={() => setPagina(p => p - 1)}
          >
            ← Anterior
          </button>

          <span style={s.paginaInfo}>
            Página {pagina + 1} de {totalPaginas}
          </span>

          <button
            style={s.btnPag}
            disabled={pagina >= totalPaginas - 1}
            onClick={() => setPagina(p => p + 1)}
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Estilos ──────────────────────────────────────────────────────────────────

const s = {
  page: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: 'system-ui, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  titulo: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  subtitulo: {
    fontSize: '13px',
    color: '#6B7280',
    margin: '4px 0 0',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
  },
  badgeDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: '#10B981',
    display: 'inline-block',
  },
  filtros: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  select: {
    padding: '7px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '13px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  input: {
    padding: '7px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#374151',
    background: '#fff',
    minWidth: '160px',
  },
  btnLimpar: {
    padding: '7px 14px',
    border: '1px solid #FCA5A5',
    borderRadius: '6px',
    background: '#FEF2F2',
    color: '#DC2626',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  erroBox: {
    background: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#DC2626',
    marginBottom: '16px',
    fontSize: '14px',
  },
  tabelaWrap: {
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    overflow: 'hidden',
    overflowX: 'auto',
  },
  tabela: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    background: '#F9FAFB',
    padding: '10px 14px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#6B7280',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #E5E7EB',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #F3F4F6',
  },
  td: {
    padding: '10px 14px',
    color: '#374151',
    verticalAlign: 'middle',
  },
  tdCentro: {
    padding: '32px',
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: '14px',
  },
  data: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#111827',
  },
  hora: {
    fontSize: '11px',
    color: '#9CA3AF',
  },
  pill: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  email: {
    fontSize: '13px',
    color: '#1D4ED8',
  },
  role: {
    fontSize: '12px',
    color: '#6B7280',
    fontStyle: 'italic',
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#4B5563',
  },
  btnDetalhe: {
    background: 'none',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    padding: '2px 8px',
    cursor: 'pointer',
    color: '#6B7280',
    fontSize: '11px',
  },
  tdDetalhe: {
    padding: '0',
    background: '#F8FAFC',
    borderBottom: '1px solid #E5E7EB',
  },
  pre: {
    margin: 0,
    padding: '12px 20px',
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#374151',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  paginacao: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '20px',
  },
  btnPag: {
    padding: '7px 16px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    background: '#fff',
    color: '#374151',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  paginaInfo: {
    fontSize: '13px',
    color: '#6B7280',
  },
};
